import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { TV_DATA_QUERY_KEY } from '@/hooks/useTvData'
import TvListingImage from '@/components/TvListingImage'

export const USER_FAVOURITE_CHANNELS_QUERY_KEY = ['user-favourite-channels'] as const

interface DisplayedChannel {
  id: string
  channel_id: string
  name: string
  icon_url: string | null
  is_displayed: boolean
}

interface FavouriteChannelsProps {
  userId: string
}

export default function FavouriteChannels({ userId }: FavouriteChannelsProps) {
  const queryClient = useQueryClient()

  const { data: displayedChannels = [], isLoading: loadingDisplayed, error: errorDisplayed } = useQuery({
    queryKey: ['listing-channel-display', 'displayed'],
    queryFn: async (): Promise<DisplayedChannel[]> => {
      const { data, error } = await supabase
        .from('listing_channel_display')
        .select('id, channel_id, name, icon_url, is_displayed')
        .eq('is_displayed', true)
        .order('name')
      if (error) throw error
      return (data ?? []) as DisplayedChannel[]
    },
    enabled: !!userId,
  })

  const { data: favouriteRows = [], isLoading: loadingFavourites, error: errorFavourites } = useQuery({
    queryKey: [...USER_FAVOURITE_CHANNELS_QUERY_KEY, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_favourite_channels')
        .select('channel_id')
        .eq('user_id', userId)
      if (error) throw error
      return data ?? []
    },
    enabled: !!userId,
  })

  const favouriteChannelIds = new Set(favouriteRows.map((r) => r.channel_id))
  const displayedChannelIds = new Set(displayedChannels.map((c) => c.channel_id))
  const favouriteIdsList = Array.from(favouriteChannelIds)

  const { data: favouriteChannelsMeta = [] } = useQuery({
    queryKey: ['listing-channel-display', 'by-ids', [...favouriteIdsList].sort().join(',')],
    queryFn: async (): Promise<DisplayedChannel[]> => {
      if (favouriteIdsList.length === 0) return []
      const { data, error } = await supabase
        .from('listing_channel_display')
        .select('id, channel_id, name, icon_url, is_displayed')
        .in('channel_id', favouriteIdsList)
      if (error) throw error
      return (data ?? []) as DisplayedChannel[]
    },
    enabled: favouriteIdsList.length > 0,
  })

  const addMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const { error } = await supabase
        .from('user_favourite_channels')
        .upsert(
          { user_id: userId, channel_id: channelId },
          { onConflict: 'user_id,channel_id', ignoreDuplicates: true }
        )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...USER_FAVOURITE_CHANNELS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const { error } = await supabase
        .from('user_favourite_channels')
        .delete()
        .eq('user_id', userId)
        .eq('channel_id', channelId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...USER_FAVOURITE_CHANNELS_QUERY_KEY, userId] })
      queryClient.invalidateQueries({ queryKey: TV_DATA_QUERY_KEY })
    },
  })

  const hiddenFavourites = favouriteIdsList.filter((id) => !displayedChannelIds.has(id))
  const hiddenFavouritesWithMeta = hiddenFavourites.map((channelId) => {
    const meta = favouriteChannelsMeta.find((c) => c.channel_id === channelId)
    return { channel_id: channelId, name: meta?.name ?? channelId }
  })

  const isLoading = loadingDisplayed || loadingFavourites
  const hasError = errorDisplayed ?? errorFavourites

  const handleChannelClick = (channelId: string) => {
    if (favouriteChannelIds.has(channelId)) {
      removeMutation.mutate(channelId)
    } else {
      addMutation.mutate(channelId)
    }
  }

  return (
    <section
      className="rounded-lg p-4 border-2 mb-4"
      style={{
        borderColor: 'var(--bg-tertiary)',
        backgroundColor: 'var(--bg-primary)',
      }}
      aria-labelledby="favourite-channels-heading"
    >
      <h2
        id="favourite-channels-heading"
        className="text-lg font-bold mb-1"
        style={{ color: 'var(--text-primary)' }}
      >
        Favourite channels
      </h2>
      <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
        Click a channel to add or remove it from your favourites. Favourites appear first in the TV Listings.
      </p>

      {hasError && (
        <p className="text-xs mb-2" style={{ color: 'var(--color-error)' }} role="alert">
          Failed to load channels. Please try again.
        </p>
      )}

      {isLoading && (
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Loading…
        </p>
      )}

      {!isLoading && !hasError && displayedChannels.length === 0 && (
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          No channels available. Displayed channels are set by an administrator in Listings Control.
        </p>
      )}

      {!isLoading && !hasError && displayedChannels.length > 0 && (
        <div className="space-y-4">
          {/* Hidden favourites: no longer displayed */}
          {hiddenFavouritesWithMeta.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                No longer displayed
              </h3>
              <ul className="flex flex-wrap gap-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {hiddenFavouritesWithMeta.map((ch) => (
                  <li key={ch.channel_id}>
                    <span
                      className="text-xs px-2 py-0.5 rounded border inline-flex items-center gap-1"
                      style={{
                        borderColor: 'var(--bg-tertiary)',
                        color: 'var(--text-secondary)',
                        opacity: 0.8,
                      }}
                    >
                      {ch.name}
                      <button
                        type="button"
                        onClick={() => removeMutation.mutate(ch.channel_id)}
                        disabled={removeMutation.isPending}
                        className="text-xs rounded px-1 hover:underline"
                        style={{ color: 'var(--text-secondary)' }}
                        aria-label={`Remove ${ch.name} from favourites`}
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Channel buttons: same layout as TV Listings (logo + name), 4 columns; highlight only the channel name when favourite */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-2"
            role="group"
            aria-label="Channels to add or remove from favourites"
          >
            {displayedChannels.map((ch) => {
              const isFavourite = favouriteChannelIds.has(ch.channel_id)
              return (
                <button
                  key={ch.channel_id}
                  type="button"
                  onClick={() => handleChannelClick(ch.channel_id)}
                  disabled={addMutation.isPending || removeMutation.isPending}
                  className="flex items-center w-full gap-2 rounded-lg border-2 border-[var(--bg-tertiary)] min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-focus)] transition-colors"
                  style={{
                    padding: '0.4rem 0.6rem',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                  }}
                  aria-pressed={isFavourite}
                  aria-label={isFavourite ? `${ch.name}, favourite. Click to remove.` : `${ch.name}. Click to add as favourite.`}
                >
                  {/* Channel logo or InsideAccess fallback */}
                  <span className="flex-shrink-0" aria-hidden>
                    <TvListingImage
                      src={ch.icon_url}
                      alt=""
                      width={32}
                      height={32}
                      objectFit="contain"
                    />
                  </span>
                  {/* Channel name: highlight only when favourite */}
                  <span
                    className="min-w-0 truncate text-sm font-medium rounded"
                    style={{
                      padding: '0.2rem 0.5rem',
                      backgroundColor: isFavourite ? 'var(--bg-interactive)' : 'transparent',
                      color: isFavourite ? 'var(--text-inverse)' : 'var(--text-primary)',
                    }}
                  >
                    {ch.name}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}
